import { TicketType as TicketTypeValues } from '../../../constants/tickets';
import { Event } from '../../../types/event';
import { Membership } from '../../../types/membership';
import { Ticket } from '../../../types/ticket';
const { HALF_ENTRY, FAN_MEMBER, CHECKIN_MEMBER, DEPENDENT, COMPANION } = TicketTypeValues;

export interface ValidationResult {
  disableAddToCart: boolean;
  errorMessage: string | null;
}
/**
 * Default validation strategy for ticket purchases.
 * Applies validation when no other specific strategy is selected.
 *
 * @extends ValidationStrategy
 */
export class ValidationStrategy {
  /**
   * Determines if this strategy should be applied.
   *
   * @param {Membership | null} membershipApplied - The membership being applied, if any
   * @param {Event | null} event - The event being validated
   * @returns {boolean} Returns false as this is the base strategy implementation
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  shouldApply(_membershipApplied: Membership, _event: Event) {
    return false; // Define quando esta estratégia deve ser aplicada
  }

  /**
   * Validates ticket purchase based on event's CPF ticket limit.
   *
   * @param {Membership | null} membershipApplied - The membership applied, if any.
   * @param {Ticket[]} cart - The current cart containing tickets.
   * @param {Event | null} event - The event associated with the tickets.
   * @param {Ticket[]} tickets - The list of all available tickets for the event.
   * @param {number} groupId - The ID of the ticket group.
   * @param {number} ticketId - The ID of the specific ticket being validated.
   * @param {number} totalTickets - The total number of tickets currently in the cart.
   *
   * @returns {object} An object containing:
   *  - `disableAddToCart`: A boolean indicating whether adding more tickets is disabled.
   *  - `errorMessage`: A string containing an error message, if applicable (null if no error).
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public validate(
    membershipApplied: Membership | null,
    cart: Ticket[],
    event: Event | null,
    tickets: Ticket[],
    groupId: number,
    ticketId: number,
    _totalTickets: number
  ): ValidationResult {

    if (!this.hasBenefitsInCart(cart)) {
      return this.getDefaultValidationResult();
    }

    if (!this.isRelevantCategoryTicket(tickets, groupId)) {
      return this.getDefaultValidationResult();
    }
    const dependentOrCompanionTicket = this.hasBenefitsInCart(cart);
    if (!dependentOrCompanionTicket) return this.getDefaultValidationResult();

    if (this.hasDifferentTypeWithRelevantCategory(
      tickets, ticketId, dependentOrCompanionTicket
    )) {
      return this.getBlockedValidationResult();
    }

    return this.getDefaultValidationResult();
  }

  private readonly CATEGORY_IDS = {
    COMPANION,
    DEPENDENT,
    CHECKIN_MEMBER,
    FAN_MEMBER,
    HALF_ENTRY
  } as const;

  // Common validation results
  private getDefaultValidationResult(): ValidationResult {
    return { disableAddToCart : false, errorMessage : null };
  }

  private getBlockedValidationResult(): ValidationResult {
    return { disableAddToCart : true, errorMessage : null };
  }

  // Category check utilities
  private isCategoryType(categoryId: number, type: keyof typeof this.CATEGORY_IDS): boolean {
    return Number(categoryId) === this.CATEGORY_IDS[type];
  }

  private isDependentTicket(categoryId: number): boolean {
    return this.isCategoryType(categoryId, 'DEPENDENT');
  }

  private isBenefitTicket(categoryId: number): boolean {
    return this.isCategoryType(categoryId, 'CHECKIN_MEMBER') ||
      this.isCategoryType(categoryId, 'FAN_MEMBER') ||
      this.isCategoryType(categoryId, 'HALF_ENTRY');
  }

  // Cart validation methods
  private hasBenefitsInCart(cart: Ticket[]): boolean {
    const result = cart.some((group) =>
      group.type.some((type) => this.isBenefitTicket(Number(type.category.id))));
    return result;
  }

  // Ticket validation methods
  private isRelevantCategoryTicket(tickets: Ticket[], groupId: number): boolean {
    return tickets.some((group) =>
      group.id === groupId &&
      group.type.some((type) =>
        this.isDependentTicket(Number(type.category.id)) ||
        this.isBenefitTicket(Number(type.category.id))
      )
    );
  }

  private hasDifferentTypeWithRelevantCategory(
    tickets: Ticket[],
    ticketId: number,
    containedTicket: boolean
  ): boolean {

    const result = tickets.some((group) =>
      group.type.some((type) => {
        const isRelevant = this.isDependentTicket(Number(type.category.id))
        || this.isBenefitTicket(Number(type.category.id));

        if (type.id === ticketId) {
          return containedTicket && isRelevant;
        }
        return false;
      })
    );
    return result;
  }

}

